import React, { useState, useEffect } from "react";
import { ArrowDownUp, AlertCircle, CheckCircle, Layers } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { TriggerInput } from "./TriggerInput";

interface SelectedProvider {
  id: string;
  url: string;
  name: string;
}

interface TriggerProviderVariableMappingSectionProps {
  questions: Array<{ id?: string; text: string; shortname?: string }>;
  selectedProviders: SelectedProvider[];
  getVariableName: (questionId: string, providerId?: string) => string;
  onVariableNameChange: (questionId: string, value: string, providerId?: string) => void;
}

// Validate POST variable names
const isValidVariableName = (name: string): boolean => {
  if (!name) return true; // Empty names are valid (will be omitted)

  // Variable names should follow snake_case pattern
  // Allow letters, numbers, and underscores
  // Must start with a letter
  // No spaces allowed
  const validPattern = /^[a-z][a-z0-9_]*$/;
  return validPattern.test(name);
};

interface ValidationState {
  [key: string]: boolean; // key is "questionId-providerId" or just "questionId" for global
}

export const TriggerProviderVariableMappingSection: React.FC<TriggerProviderVariableMappingSectionProps> = ({
  questions,
  selectedProviders,
  getVariableName,
  onVariableNameChange,
}) => {
  const [validationState, setValidationState] = useState<ValidationState>({});
  const [activeTab, setActiveTab] = useState<string>("global");

  // Validate all variable names when questions, providers, or mappings change
  useEffect(() => {
    const newValidationState: ValidationState = {};

    // Validate global mappings
    questions.forEach(question => {
      const id = question.id || '';
      const value = getVariableName(id);
      newValidationState[id] = isValidVariableName(value);
    });

    // Validate provider-specific mappings
    selectedProviders.forEach(provider => {
      questions.forEach(question => {
        const id = question.id || '';
        const value = getVariableName(id, provider.id);
        newValidationState[`${id}-${provider.id}`] = isValidVariableName(value);
      });
    });

    setValidationState(newValidationState);
  }, [questions, selectedProviders, getVariableName]);

  // Handle variable name change with validation
  const handleVariableNameChange = (questionId: string, value: string, providerId?: string) => {
    // Update validation state
    const stateKey = providerId ? `${questionId}-${providerId}` : questionId;
    setValidationState(prev => ({
      ...prev,
      [stateKey]: isValidVariableName(value)
    }));

    // Pass to parent component
    onVariableNameChange(questionId, value, providerId);
  };

  // Ensure the active tab exists (in case a provider was removed)
  useEffect(() => {
    if (activeTab !== "global" && !selectedProviders.some(p => p.id === activeTab)) {
      setActiveTab("global");
    }
  }, [selectedProviders, activeTab]);

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h4 className="text-sm font-medium text-gray-700 flex items-center">
          <ArrowDownUp className="h-4 w-4 mr-1" />
          POST Variable Mappings
        </h4>
      </div>

      {selectedProviders.length > 0 ? (
        <>
          <div className="flex items-center gap-2">
            <Layers className="h-4 w-4 text-gray-500" />
            <p className="text-xs text-gray-500">
              Configure variable mappings for each provider or use global mappings for all providers.
            </p>
          </div>

          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="mb-4">
              <TabsTrigger value="global">
                Global Mappings
                <Badge variant="outline" className="ml-2 text-xs">All Providers</Badge>
              </TabsTrigger>
              {selectedProviders.map(provider => (
                <TabsTrigger key={provider.id} value={provider.id}>
                  {provider.name}
                </TabsTrigger>
              ))}
            </TabsList>

            <TabsContent value="global" className="mt-0">
              <div className="p-3 bg-blue-50 rounded-md mb-4">
                <p className="text-xs text-blue-700">
                  Global mappings will be used for all providers unless overridden with provider-specific mappings.
                </p>
              </div>

              {renderVariableMappingTable(
                questions,
                getVariableName,
                handleVariableNameChange,
                validationState,
                undefined
              )}
            </TabsContent>

            {selectedProviders.map(provider => (
              <TabsContent key={provider.id} value={provider.id} className="mt-0">
                <div className="p-3 bg-blue-50 rounded-md mb-4 flex items-start">
                  <AlertCircle className="h-4 w-4 mr-2 mt-0.5 flex-shrink-0 text-blue-700" />
                  <div>
                    <p className="text-xs text-blue-700">
                      These mappings will only be used for <strong>{provider.name}</strong>.
                      Leave empty to use global mappings.
                    </p>
                  </div>
                </div>

                {renderVariableMappingTable(
                  questions,
                  getVariableName,
                  handleVariableNameChange,
                  validationState,
                  provider.id
                )}
              </TabsContent>
            ))}
          </Tabs>
        </>
      ) : (
        <>
          <p className="text-xs text-gray-500">
            Map questions to POST variables that will be sent to your webhook URL. Use snake_case format (e.g., first_name, email_address).
          </p>

          {renderVariableMappingTable(
            questions,
            getVariableName,
            handleVariableNameChange,
            validationState,
            undefined
          )}
        </>
      )}
    </div>
  );
};

// Helper function to render the variable mapping table
const renderVariableMappingTable = (
  questions: Array<{ id?: string; text: string; shortname?: string }>,
  getVariableName: (questionId: string, providerId?: string) => string,
  handleVariableNameChange: (questionId: string, value: string, providerId?: string) => void,
  validationState: ValidationState,
  providerId?: string
) => {
  if (questions.length === 0) {
    return (
      <div className="text-sm text-gray-500 italic">
        No questions available for mapping. Add questions in the Flow tab.
      </div>
    );
  }

  return (
    <div className="grid grid-cols-3 gap-4">
      <div className="text-sm font-medium text-gray-700 p-2">Question</div>
      <div className="text-sm font-medium text-gray-700 p-2">Key Name</div>
      <div className="text-sm font-medium text-gray-700 p-2">POST Variable Name</div>

      {questions.map((question) => {
        const questionId = question.id || '';
        const stateKey = providerId ? `${questionId}-${providerId}` : questionId;
        const value = getVariableName(questionId, providerId);
        const isValid = validationState[stateKey];

        return (
          <React.Fragment key={`${questionId}-${providerId || 'global'}`}>
            <div className="p-2 bg-gray-50 rounded text-sm">
              {question.text}
            </div>
            <div>
              <input
                type="text"
                value={question.shortname || ''}
                disabled
                className="w-full text-sm p-2 border rounded bg-gray-50 text-gray-600"
                placeholder="No shortname"
              />
            </div>
            <div>
              <div className="relative">
                <TriggerInput
                  initialValue={value}
                  onSave={(newValue) => handleVariableNameChange(questionId, newValue, providerId)}
                  className={`w-full text-sm p-2 pr-9 border rounded ${
                    value && !isValid
                      ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
                      : value
                        ? 'border-green-300 focus:ring-green-500 focus:border-green-500'
                        : ''
                  }`}
                  placeholder={question.shortname || "user_response"}
                  validationFn={isValidVariableName}
                  showValidation={true}
                />
                {value && (
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                    {isValid ? (
                      <CheckCircle className="h-4 w-4 text-green-500" />
                    ) : (
                      <AlertCircle className="h-4 w-4 text-red-500" />
                    )}
                  </div>
                )}
              </div>
              {value && !isValid && (
                <p className="text-xs text-red-500 mt-1">
                  Use snake_case (e.g., user_response). Start with a letter, use only lowercase letters, numbers, and underscores.
                </p>
              )}
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
};
